import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/core/http/client';
import { usePrincipal } from '@/core/auth/appToken';
import type { ApiError } from '@/models/apiError';
import type { components } from '@/generated/api/schema';
import { customersKeys } from './customers.keys';

/** Wire response for the open-assignment-count endpoint. */
export type OpenAssignmentCountResponse = components['schemas']['OpenAssignmentCountResponse'];

/**
 * The three DELABO-lab roles the "Handlungsbedarf" KPI count is visible to (US-E10-07, AK-2) —
 * unlike confirm/reject, which is restricted to Geschäftsführung/Leitung, this read-only count is
 * visible to every DELABO-lab role including `DELABO_LAB_EMPLOYEE` (the E10-08 11-role model
 * collapsed the former Verwaltung/Techniker roles into `DELABO_LAB_EMPLOYEE`).
 */
const DELABO_LAB_ROLES: readonly string[] = [
  'DELABO_LAB_CEO',
  'DELABO_LAB_LEAD',
  'DELABO_LAB_EMPLOYEE',
];

/** Poll interval keeping the badge current for assignments handled outside this session. */
const OPEN_ASSIGNMENT_COUNT_POLL_INTERVAL_MS = 60_000;

async function fetchOpenAssignmentCount(): Promise<OpenAssignmentCountResponse> {
  // Non-2xx responses (401/403) become a typed ApiError before this line is reached — see
  // client.ts. Scoped server-side to the caller's own DELABO lab; no id is ever sent.
  const { data } = await apiClient.GET('/api/v1/customers/delabo-labor/open-assignment-count');
  return data as OpenAssignmentCountResponse;
}

/**
 * Fetches the number of customer organizations currently awaiting partner-lab confirmation by the
 * caller's own DELABO lab (US-E10-07, AK-2) — backs the "Handlungsbedarf" KPI tile on the
 * DELABO-lab dashboard (Work-Item 12).
 *
 * `enabled` is gated on the caller holding one of the three {@link DELABO_LAB_ROLES}: the endpoint
 * is meaningless (and forbidden) for any other role, so the query never fires for a principal it
 * cannot resolve a value for. This is purely a fetch-avoidance optimization, not a security
 * boundary — the binding enforcement is the backend's own role check.
 */
export function useOpenAssignmentCount(): UseQueryResult<OpenAssignmentCountResponse, ApiError> {
  const principal = usePrincipal();
  const enabled = principal !== null && DELABO_LAB_ROLES.includes(principal.role);
  const queryClient = useQueryClient();

  // TanStack only listens to `visibilitychange`; clicking into a side-by-side window never fires it.
  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => {
      const status = queryClient.getQueryState<unknown, ApiError>(
        customersKeys.openAssignmentCount(),
      )?.error?.status;
      if (status === 401 || status === 403) return;
      void queryClient.refetchQueries({
        queryKey: customersKeys.openAssignmentCount(),
        type: 'active',
      });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, queryClient]);

  const query = useQuery<OpenAssignmentCountResponse, ApiError>({
    queryKey: customersKeys.openAssignmentCount(),
    queryFn: fetchOpenAssignmentCount,
    enabled,
    refetchInterval: (q) => {
      const status = q.state.error?.status;
      if (status === 401 || status === 403) return false;
      return OPEN_ASSIGNMENT_COUNT_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
  });

  // A changed count means a customer's state changed elsewhere, so the customer list is stale too.
  const count = query.data?.count;
  const previousCount = useRef(count);
  useEffect(() => {
    if (
      previousCount.current !== undefined &&
      count !== undefined &&
      count !== previousCount.current
    ) {
      void queryClient.invalidateQueries({ queryKey: customersKeys.delaboLaborAll() });
    }
    previousCount.current = count;
  }, [count, queryClient]);

  return query;
}
